import { m, Variants } from "framer-motion";
import { ReactNode, useRef } from "react";

// One easing curve for the whole app. A steep-then-settling deceleration
// (easeOutExpo-ish) reads as deliberate rather than bouncy — it matches the
// field-journal type system better than a spring would.
export const EASE = [0.22, 1, 0.36, 1] as const;

const DISTANCE = 12;

export const fadeUp: Variants = {
  hidden: { opacity: 0, y: DISTANCE },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE } },
};

// The container itself doesn't animate; it only sequences its children, so a
// list resolves top-to-bottom instead of every row appearing at once.
export const staggerContainer: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.045, delayChildren: 0.03 } },
};

/**
 * Whether content appearing right now deserves an entrance animation.
 *
 * Pass the query's loading flag. This only looks at the value on the *first*
 * render of the mount: if the page started with no data, the skeleton→content
 * swap is a real transition and gets animated. If react-query already had the
 * data cached — switching back to a tab you've already visited — the first
 * render is the finished content, so it appears instantly instead of
 * replaying the cascade for something that never actually loaded.
 */
export function useEnterAnimation(isLoading: boolean): boolean {
  const startedWithoutData = useRef(isLoading);
  return startedWithoutData.current;
}

/**
 * A single element that fades and lifts into place on mount. `enabled={false}`
 * renders it at its final state with no animation.
 */
export function FadeIn({
  children,
  className = "",
  delay = 0,
  enabled = true,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  enabled?: boolean;
}) {
  return (
    <m.div
      className={className}
      // `initial={false}` tells framer-motion to render straight at the
      // `animate` values rather than transitioning to them.
      initial={enabled ? { opacity: 0, y: DISTANCE } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: EASE, delay }}
    >
      {children}
    </m.div>
  );
}

/** Wraps a list so its <StaggerItem> children resolve one after another. */
export function Stagger({
  children,
  className = "",
  enabled = true,
}: {
  children: ReactNode;
  className?: string;
  enabled?: boolean;
}) {
  return (
    <m.div
      className={className}
      variants={staggerContainer}
      initial={enabled ? "hidden" : false}
      animate="show"
    >
      {children}
    </m.div>
  );
}

/** One row inside a <Stagger>. */
export function StaggerItem({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <m.div className={className} variants={fadeUp}>
      {children}
    </m.div>
  );
}
